import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, of } from 'rxjs';
import { ServiceConstant } from '../utils/service-constant.util';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  httpClient = inject(HttpClient);

  constructor() {}

  getTodayQuote(): Observable<any> {
    return this.httpClient.get<any>(ServiceConstant.apiUrl + '/generative/quote').pipe(
      catchError((error) => {
        console.error('Error fetching today quotes', error);
        return of(error);
      }),
    );
  }

  getMenuStats(period: any): Observable<any> {
    // return this.httpClient.get<any>(ServiceConstant.apiUrl + '/recommendations/stats/highest-menu?period_type='+period)
    //   .pipe(catchError(error => {
    //       console.error('Error fetching menu vise stats', error);
    //       return of(error);
    //     })
    //   );
    return this.httpClient
      .get<any>(ServiceConstant.apiUrl + '/stats/widgets/highest-menu?window=' + period)
      .pipe(
        catchError((error) => {
          console.error('Error fetching menu vise stats', error);
          return of(error);
        }),
      );
  }

  getRestaurantStat(period: any): Observable<any> {
    return this.httpClient
      .get<any>(ServiceConstant.apiUrl + '/recommendations/stats?period_type=' + period)
      .pipe(
        catchError((error) => {
          console.error('Error fetching restaurant vise stats', error);
          return of(error);
        }),
      );
  }

  getInprogressOrders(): Observable<any> {
    return this.httpClient
      .get<any>(
        ServiceConstant.apiUrl +
          '/restaurants/orders?delta_hours=1000000&limit=100&order_status=in_progress',
      )
      .pipe(
        catchError((error) => {
          console.error('Error fetching orders by inprogress status', error);
          return of(error);
        }),
      );
  }

  getMenusByPromotion(): Observable<any> {
    return this.httpClient
      .get<any>(ServiceConstant.apiUrl + '/restaurants/menus/menu-promotions')
      .pipe(
        catchError((error) => {
          console.error('Error fetching menus by promotions', error);
          return of(error);
        }),
      );
  }

  getChartOrderSummary(period: any, metric: string = 'orders'): Observable<any> {
    return this.httpClient
      .get<any>(
        ServiceConstant.apiUrl + '/stats/summary-chart?metric=' + metric + '&period=' + period,
      )
      .pipe(
        catchError((error) => {
          console.error('Error fetching chart order summary', error);
          return of(error);
        }),
      );
  }

  getAiInsights(): Observable<any> {
    return this.httpClient.get<any>(ServiceConstant.apiUrl + '/ai-insights/insights-list').pipe(
      catchError((error) => {
        console.error('Error fetching AI insights', error);
        return of(error);
      }),
    );
  }

  chatWitAgent(prompt: any, restaurantId: string, insightId: any): Observable<any> {
    return this.httpClient
      .post<any>(
        ServiceConstant.apiUrl +
          '/restaurants/' +
          restaurantId +
          '/insights/' +
          insightId +
          '/chat/messages',
        prompt,
      )
      .pipe(
        catchError((error) => {
          console.error('Error chatting with agent', error);
          return of(error);
        }),
      );
  }

  getChatMessages(
    restaurantId: string,
    insightId: string,
    cursor: string = '',
    limit: number = 50,
  ): Observable<any> {
    const params = new HttpParams().set('limit', String(limit)).set('cursor', cursor ?? '');
    return this.httpClient
      .get<any>(
        `${ServiceConstant.apiUrl}/restaurants/${restaurantId}/insights/${insightId}/chat/messages`,
        { params },
      )
      .pipe(
        catchError((error) => {
          console.error('Error fetching chat messages', error);
          return of(error);
        }),
      );
  }

  initiateChat(restaurantId: string, insightId: any): Observable<any> {
    return this.httpClient
      .post<any>(
        ServiceConstant.apiUrl +
          '/restaurants/' +
          restaurantId +
          '/insights/' +
          insightId +
          '/chat',
        {},
      )
      .pipe(
        catchError((error) => {
          console.error('Error initiating chat', error);
          return of(error);
        }),
      );
  }

  initiateCommonChat(restaurantId: string): Observable<any> {
    return this.httpClient
      .post<any>(ServiceConstant.apiUrl + '/restaurants/' + restaurantId + '/chats', {})
      .pipe(
        catchError((error) => {
          console.error('Error initiating common chat', error);
          return of(error);
        }),
      );
  }

  chatWithCommonAgent(restaurantId: string, chatId: string, message: any): Observable<any> {
    return this.httpClient
      .post<any>(
        ServiceConstant.apiUrl + '/restaurants/' + restaurantId + '/chats/' + chatId + '/messages',
        message,
      )
      .pipe(
        catchError((error) => {
          console.error('Error chatting with common agent', error);
          return of(error);
        }),
      );
  }

  getCommonChatMessages(
    restaurantId: string,
    chatId: string,
    cursor: string = '',
    limit: number = 50,
  ): Observable<any> {
    const params = new HttpParams().set('limit', String(limit)).set('cursor', cursor ?? '');
    return this.httpClient
      .get<any>(`${ServiceConstant.apiUrl}/restaurants/${restaurantId}/chats/${chatId}/messages`, {
        params,
      })
      .pipe(
        catchError((error) => {
          console.error('Error fetching common chat messages', error);
          return of(error);
        }),
      );
  }

  updateCommonChat(restaurantId: string, chatId: string, data: any): Observable<any> {
    return this.httpClient
      .patch<any>(
        ServiceConstant.apiUrl + '/restaurants/' + restaurantId + '/chats/' + chatId,
        data,
      )
      .pipe(
        catchError((error) => {
          console.error('Error patching common chat', error);
          return of(error);
        }),
      );
  }

  deleteChat(restaurantId: string, chatId: string): Observable<any> {
    return this.httpClient
      .delete<any>(`${ServiceConstant.apiUrl}/restaurants/${restaurantId}/chats/${chatId}`)
      .pipe(
        catchError((error) => {
          console.error('Error deleting chat', error);
          return of(error);
        }),
      );
  }

  deleteChatMessage(restaurantId: string, chatId: string, messageId: string): Observable<any> {
    return this.httpClient
      .delete<any>(
        ServiceConstant.apiUrl +
          '/restaurants/' +
          restaurantId +
          '/chats/' +
          chatId +
          '/messages/' +
          messageId,
      )
      .pipe(
        catchError((error) => {
          console.error('Error deleting chat message', error);
          return of(error);
        }),
      );
  }

  getAllChats(restaurantId: string, cursor: string = '', limit: number = 20): Observable<any> {
    const params = new HttpParams().set('limit', String(limit)).set('cursor', cursor ?? '');
    return this.httpClient
      .get<any>(ServiceConstant.apiUrl + '/restaurants/' + restaurantId + '/chats', { params })
      .pipe(
        catchError((error) => {
          console.error('Error fetching all chats', error);
          return of(error);
        }),
      );
  }
}
